import { parseDate } from 'utils/datetime';
import { NavButton } from './NavButton';
import { useStartAndEndDate } from './hooks';
import { CalendarTile } from './CalendarTile';
import { RangeCalendarProps, NavDirection, Value } from './types';
import { StyledCalendar, StyledCoreCalendar } from './Calendar.styled';

export const DateRangeCalendar = ({
  blockFutureDates,
  blockPastDates,
  endDate,
  onDatesChange,
  showNeighboringMonth,
  startDate,
  styledAsCore,
  tileContent,
  validDateRange,
  ...props
}: RangeCalendarProps) => {
  const [minDate, maxDate] = useStartAndEndDate(validDateRange);
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  const onChange = (values: Value) => {
    /**
     * we need to do some type validation here as the value type that
     * the react-calendar onChange _can_ return is broader than what it
     * actually _does_ return (in our case).
     *
     * that's because of the `allowPartialRange` prop that we've enabled,
     * which forces the onChange to always return an array, which in our case
     * is going to be filled either as [DateInput] or [DateInput, DateInput].
     *
     * That DateInput could theortetically be null, so we need to type check for that as well.
     */
    if (Array.isArray(values)) {
      onDatesChange({
        startDate: values[0] !== null ? values[0] : undefined,
        endDate: values.length === 2 && values[1] !== null ? values[1] : undefined,
      });
    }
  };

  const customTileContent =
    tileContent ||
    (({ date: tileDate }) => (
      <CalendarTile
        isSelected={tileDate.getTime() === start.getTime() || tileDate.getTime() === end.getTime()}
        date={tileDate}
      />
    ));

  /**
   * the core/datepicker and core/dateRangePicker are still used in certain places, despite being deprecated.
   * they both now use the ui/calendar for their calendar, but retain the original core/ styling.
   * don't use this version.
   */
  const Calendar = styledAsCore ? StyledCoreCalendar : StyledCalendar;

  return (
    <Calendar
      {...props}
      allowPartialRange
      calendarType="gregory" // starts the week with sunday
      maxDate={blockFutureDates ? parseDate() : maxDate}
      maxDetail="month"
      minDate={blockPastDates ? parseDate() : minDate}
      minDetail="month"
      nextLabel={<NavButton styledAsCore={styledAsCore} direction={NavDirection.Next} />}
      onChange={onChange}
      prevLabel={<NavButton styledAsCore={styledAsCore} direction={NavDirection.Prev} />}
      selectRange
      showNeighboringMonth={showNeighboringMonth}
      tileContent={customTileContent}
      value={[start, end]}
    />
  );
};
